// Eclipse mode: the viewer opened at an eclipse from the sky calendar
// (src/sky/events.js), the shadows drawn from the real geometry
// (src/eclipse/geometry.js), with a small panel to play, scrub and leave.
//
//   /viewer/#eclipse/2027-08-02   the eclipse on that UTC date (solar or lunar)
//
// main.js owns the clock, the scale and the camera and hands them in:
//   createEclipseMode({
//     getJD, setJD,              the simulation clock (Julian date, UTC)
//     setRate(simSecondsPerSecond), 0 pauses
//     enterScene(), leaveScene(),  switch to true scale and back, show the live sky
//     frame(event),               put the camera on the eclipse
//   })

import { skyEventById } from '../sky/events.js';
import { lunarUmbralMagnitude } from './geometry.js';

const DAY_MS = 86400000;
const jdOf = (ms) => ms / DAY_MS + 2440587.5;
const msOf = (jd) => (jd - 2440587.5) * DAY_MS;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// How each kind plays: where it starts relative to greatest eclipse, the
// window the scrubber spans, and the default pace (sim seconds per second).
const KINDS = {
  'eclipse-solar': { leadMin: 12, spanMin: 150, rate: 60 },
  'eclipse-lunar': { leadMin: 40, spanMin: 200, rate: 300 },
};
const RATES = [[1, 'Real time'], [60, '1 min/s'], [300, '5 min/s']];

// '#eclipse/2027-08-02' -> the eclipse on that date, or null.
export function eclipseFromHash(hash) {
  const m = /^#\/?eclipse\/(\d{4}-\d{2}-\d{2})\/?$/.exec(String(hash || ''));
  if (!m) return null;
  return skyEventById(`eclipse-solar-${m[1]}`) || skyEventById(`eclipse-lunar-${m[1]}`);
}

export function eclipseHash(event) {
  return `#eclipse/${event.at.slice(0, 10)}`;
}

const CSS = `
.ecl-panel {
  --ecl-ink: #ece7da; --ecl-dim: #a8a396; --ecl-mute: #a8a396;
  --ecl-fill: #141922; --ecl-fill-hover: #1b212c; --ecl-amber: #e0a851;
  position: fixed; left: 50%; bottom: max(18px, env(safe-area-inset-bottom)); z-index: 175;
  width: min(600px, calc(100vw - 32px)); transform: translate(-50%, 12px); opacity: 0;
  padding: 16px 18px 14px; box-sizing: border-box;
  background: #0a0d13; border: none; border-radius: 12px;
  color: var(--ecl-ink); font-family: 'Inter', sans-serif; font-size: 13px;
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.ecl-panel.visible { opacity: 1; transform: translate(-50%, 0); }
.ecl-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.ecl-kicker { font: 500 14px/1.3 'Inter', sans-serif; color: var(--ecl-ink); }
.ecl-where { margin-top: 3px; color: var(--ecl-dim); font-size: 12px; line-height: 1.45; }
.ecl-time { display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px 12px; margin: 12px 0 2px; }
.ecl-clock { font: 500 22px/1.2 'Inter', sans-serif; font-variant-numeric: tabular-nums; }
.ecl-status { color: var(--ecl-ink); font-size: 13px; }
.ecl-local { color: var(--ecl-mute); font-size: 12px; }
.ecl-row { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 12px; }
.ecl-btn { appearance: none; border: none; background: var(--ecl-fill); color: var(--ecl-ink);
  font: 500 12px 'Inter', sans-serif; min-height: 36px; padding: 0 12px; border-radius: 8px; cursor: pointer; line-height: 1;
  transition: background-color 0.15s ease; }
.ecl-btn:hover { background: var(--ecl-fill-hover); }
.ecl-btn:focus-visible { outline: 2px solid #7dd3a8; outline-offset: 2px; }
.ecl-btn[aria-pressed="true"] { background: #15302a; color: var(--ecl-ink); }
.ecl-play { min-width: 72px; }
.ecl-top-actions { display: flex; gap: 6px; flex: none; }
.ecl-rates { display: inline-flex; gap: 4px; margin-left: auto; }
.ecl-scrub { width: 100%; margin: 14px 0 0; height: 18px; -webkit-appearance: none; appearance: none; background: none; cursor: pointer; }
.ecl-scrub::-webkit-slider-runnable-track { height: 4px; border-radius: 2px; background: #232a36; }
.ecl-scrub::-webkit-slider-thumb { -webkit-appearance: none; width: 14px; height: 14px; margin-top: -5px; border-radius: 50%; background: var(--ecl-ink); }
.ecl-scrub::-moz-range-track { height: 4px; border-radius: 2px; background: #232a36; }
.ecl-scrub::-moz-range-thumb { width: 14px; height: 14px; border: none; border-radius: 50%; background: var(--ecl-ink); }
.ecl-scrub:focus-visible { outline: 2px solid #7dd3a8; outline-offset: 2px; }
.ecl-scale { display: flex; justify-content: space-between; color: var(--ecl-mute); font-size: 11px; margin-top: 4px; font-variant-numeric: tabular-nums; }
body.eclipse-mode #controls-hint, body.eclipse-mode #event-caption { display: none !important; }
body.eclipse-mode #planet-nav { opacity: 0; pointer-events: none; }
@media (max-width: 760px) { body.eclipse-mode #mobile-menu-fab { display: none !important; } }
@media (max-width: 560px) {
  .ecl-panel { padding: 12px 12px 10px; }
  .ecl-clock { font-size: 18px; }
  .ecl-rates { margin-left: 0; }
}
`;

function el(tag, attrs = {}, kids = []) {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') e.className = v;
    else if (k === 'text') e.textContent = v;
    else if (k.startsWith('on')) e.addEventListener(k.slice(2), v);
    else e.setAttribute(k, v);
  }
  for (const c of [].concat(kids)) if (c) e.append(c);
  return e;
}

function minutesLabel(min) {
  const m = Math.round(Math.abs(min));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} h ${String(m % 60).padStart(2, '0')}`;
}

export function createEclipseMode(ctx) {
  const state = { active: false, event: null, greatestJD: 0, rate: 0, playing: false, timer: 0 };
  let panel = null;
  let parts = {};

  function injectCss() {
    if (document.getElementById('ecl-css')) return;
    document.head.append(el('style', { id: 'ecl-css', text: CSS }));
  }

  function kind() { return KINDS[state.event.kind]; }

  function setPlaying(on) {
    state.playing = on;
    ctx.setRate(on ? state.rate : 0);
    if (parts.play) {
      parts.play.textContent = on ? 'Pause' : 'Play';
      parts.play.setAttribute('aria-label', on ? 'Pause' : 'Play');
    }
  }

  function setRate(rate) {
    state.rate = rate;
    for (const b of parts.rateButtons || []) b.setAttribute('aria-pressed', String(Number(b.dataset.rate) === rate));
    if (state.playing) ctx.setRate(rate);
  }

  function jump(min) {
    const k = kind();
    const target = Math.min(Math.max(ctx.getJD() + min / 1440, state.greatestJD - k.spanMin / 1440), state.greatestJD + k.spanMin / 1440);
    ctx.setJD(target);
    refresh();
  }

  function buildPanel() {
    const e = state.event;
    const k = kind();
    const at = new Date(e.at);
    const day = `${at.getUTCDate()} ${MONTHS[at.getUTCMonth()]} ${at.getUTCFullYear()}`;
    const where = e.short.split(' · ').slice(1).join(' · ');
    parts = {};
    parts.clock = el('span', { class: 'ecl-clock' });
    parts.status = el('span', { class: 'ecl-status' });
    parts.local = el('div', { class: 'ecl-local' });
    parts.play = el('button', { class: 'ecl-btn ecl-play', type: 'button', onclick: () => setPlaying(!state.playing) });
    parts.scrub = el('input', { class: 'ecl-scrub', type: 'range', min: String(-k.spanMin), max: String(k.spanMin), step: '1', 'aria-label': 'Time around greatest eclipse' });
    parts.scrub.addEventListener('input', () => {
      setPlaying(false);
      ctx.setJD(state.greatestJD + Number(parts.scrub.value) / 1440);
      refresh();
    });
    parts.rateButtons = RATES.map(([rate, label]) => el('button', {
      class: 'ecl-btn', type: 'button', 'data-rate': String(rate), 'aria-pressed': 'false', text: label, onclick: () => setRate(rate),
    }));
    panel = el('section', { class: 'ecl-panel', role: 'region', 'aria-label': `${e.title}, ${day}` }, [
      el('div', { class: 'ecl-top' }, [
        el('div', {}, [
          el('div', { class: 'ecl-kicker', text: `${e.title} · ${day}` }),
          el('div', { class: 'ecl-where', text: where }),
        ]),
        el('div', { class: 'ecl-top-actions' }, [
          el('button', { class: 'ecl-btn', type: 'button', text: 'Reframe', title: 'Put the camera back on the eclipse', onclick: () => ctx.frame(state.event) }),
          el('button', { class: 'ecl-btn', type: 'button', text: 'Live sky', title: 'Back to the sky right now', onclick: () => exit() }),
        ]),
      ]),
      el('div', { class: 'ecl-time' }, [parts.clock, parts.status]),
      parts.local,
      el('div', { class: 'ecl-row' }, [
        el('button', { class: 'ecl-btn', type: 'button', text: '−10 min', onclick: () => jump(-10) }),
        parts.play,
        el('button', { class: 'ecl-btn', type: 'button', text: '+10 min', onclick: () => jump(10) }),
        el('button', { class: 'ecl-btn', type: 'button', text: 'Greatest', title: 'Jump to greatest eclipse', onclick: () => { setPlaying(false); ctx.setJD(state.greatestJD); refresh(); } }),
        el('span', { class: 'ecl-rates', role: 'group', 'aria-label': 'Playback speed' }, parts.rateButtons),
      ]),
      parts.scrub,
      el('div', { class: 'ecl-scale' }, [
        el('span', { text: `−${minutesLabel(k.spanMin)}` }),
        el('span', { text: 'greatest' }),
        el('span', { text: `+${minutesLabel(k.spanMin)}` }),
      ]),
    ]);
    document.body.append(panel);
    requestAnimationFrame(() => panel && panel.classList.add('visible'));
  }

  function refresh() {
    if (!state.active || !panel) return;
    const k = kind();
    const jd = ctx.getJD();
    const offMin = (jd - state.greatestJD) * 1440;
    // Stop at the end of the window instead of running off into a plain sky.
    if (state.playing && offMin >= k.spanMin) {
      ctx.setJD(state.greatestJD + k.spanMin / 1440);
      setPlaying(false);
    }
    const d = new Date(msOf(jd));
    parts.clock.textContent = `${d.toISOString().slice(11, 19)} UTC`;
    let status;
    if (Math.abs(offMin) < 0.5) status = 'Greatest eclipse';
    else status = offMin < 0 ? `Greatest eclipse in ${minutesLabel(offMin)}` : `${minutesLabel(offMin)} after greatest`;
    if (state.event.kind === 'eclipse-lunar') {
      const mag = lunarUmbralMagnitude(jd);
      const phase = mag >= 1 ? 'totality' : mag > 0 ? 'partial' : 'in the penumbra or clear';
      status += ` · ${phase}`;
    }
    parts.status.textContent = status;
    parts.local.textContent = `${d.toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })} your time`;
    if (document.activeElement !== parts.scrub) parts.scrub.value = String(Math.round(offMin));
  }

  function enter(event) {
    if (!event || !KINDS[event.kind]) return false;
    injectCss();
    if (panel) { panel.remove(); panel = null; }
    // Active before the scene changes, so the focus code that runs with it
    // knows an eclipse is being shown (and keeps its address).
    const entering = !state.active;
    state.active = true;
    if (entering) ctx.enterScene();
    state.event = event;
    state.greatestJD = jdOf(Date.parse(event.at));
    const k = kind();
    ctx.setJD(state.greatestJD - k.leadMin / 1440);
    buildPanel();
    setRate(k.rate);
    setPlaying(true);
    document.body.classList.add('eclipse-mode');
    ctx.frame(event);
    clearInterval(state.timer);
    state.timer = setInterval(refresh, 200);
    refresh();
    return true;
  }

  function exit() {
    if (!state.active) return;
    state.active = false;
    clearInterval(state.timer);
    document.body.classList.remove('eclipse-mode');
    if (panel) {
      const p = panel;
      p.classList.remove('visible');
      setTimeout(() => p.remove(), 260);
      panel = null;
    }
    if (/^#\/?eclipse\//.test(location.hash)) history.replaceState(null, '', location.pathname + location.search);
    ctx.leaveScene();
  }

  return {
    enter,
    exit,
    get active() { return state.active; },
    get event() { return state.event; },
  };
}
